const SALE_COUNTDOWN_DAY = 86400000;

/* A REAL deadline, when one is configured, wins over the rolling clock.
   data-sale-deadline is the site's single source of truth for when the sale
   ends: benchmark-launch-bundle.js and benchmark-pdp.js read the same
   attribute, so the bar, the bundle card and the PDP countdown cannot tell a
   visitor three different stories. Returns NaN when unset or unparseable,
   which drops every caller back to its own rolling behaviour. */
const saleRealDeadline = () => {
  const el = document.querySelector('[data-sale-deadline]');
  const raw = el ? el.dataset.saleDeadline : '';
  const at = raw ? new Date(raw).getTime() : NaN;
  return Number.isNaN(at) ? NaN : at;
};

// the sale bar runs a rolling deadline: once it lapses the countdown restarts on
// the next 24h cycle instead of freezing at 00:00:00
const saleCountdownDeadline = (saleEnd) => {
  const real = saleRealDeadline();
  if (!Number.isNaN(real)) return real;
  const now = Date.now();
  const configured = saleEnd ? new Date(saleEnd).getTime() : NaN;
  if (!Number.isNaN(configured)) {
    const lapsedDays = Math.max(0, Math.ceil((now - configured) / SALE_COUNTDOWN_DAY));
    return configured + lapsedDays * SALE_COUNTDOWN_DAY;
  }
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  return midnight.getTime();
};

// one ticker drives every sale bar on the page - the header band and the cart
// drawer band - so they never drift apart
const startSaleCountdown = () => {
  if (window.saleCountdownInterval) return;
  const source = document.querySelector('[data-sale-end]');
  const saleEnd = source ? source.dataset.saleEnd : '';
  const pad = (value) => String(value).padStart(2, '0');
  const tick = () => {
    const targets = document.querySelectorAll('[data-sale-countdown]');
    if (!targets.length) return;
    const remaining = Math.max(0, Math.floor((saleCountdownDeadline(saleEnd) - Date.now()) / 1000));
    /* a multi-day deadline will not fit hh:mm:ss - 4 days reads as "103:12:44"
       which looks like a bug rather than urgency, so days get their own field
       and the hours field resets under it */
    const days = Math.floor(remaining / 86400);
    const hours = Math.floor((remaining % 86400) / 3600);
    const clock = `${pad(hours)}:${pad(Math.floor((remaining % 3600) / 60))}:${pad(remaining % 60)}`;
    const text = days > 0 ? `${days}d ${clock}` : clock;
    targets.forEach((target) => {
      target.textContent = text;
      // GAH-FALL-01: a day field makes the clock ~25px wider; narrow bands
      // key off this to drop the "Ends in" words (benchmark-fall-sale.css)
      target.toggleAttribute('data-sale-days', days > 0);
    });
    /* Once a REAL deadline passes, "ENDS IN 00:00:00" is a false claim left
       running on every page. Drop the clause instead: the bar keeps the sale
       name and the discount, and simply stops promising a deadline. Taking
       the sale itself down is still a manual step - this only stops the page
       lying in the gap. The rolling clock never reaches here; it restarts. */
    if (!Number.isNaN(saleRealDeadline()) && remaining <= 0) {
      document
        .querySelectorAll('.header-benchmark__salebar-ends, .cart-drawer__salebar-ends')
        .forEach((el) => {
          el.hidden = true;
          const dot = el.previousElementSibling;
          if (dot && dot.classList.contains('header-benchmark__salebar-dot')) dot.hidden = true;
          if (dot && dot.classList.contains('cart-drawer__salebar-dot')) dot.hidden = true;
        });
      clearInterval(window.saleCountdownInterval);
      window.saleCountdownInterval = null;
    }
  };
  tick();
  window.saleCountdownInterval = setInterval(tick, 1000);
};

/* GAH-NAV-01: the moment a visitor actually opens the Shop menu, in either
   arm, once per pageview. The exposure event (GD_FLAGS.exposeNavArm in
   layout/theme.liquid) fires on every pageview because the header is
   sitewide; this second event lets the readout segment to the visitors who
   saw the cards at all. Same arm field, same values. Reads the class that
   RENDERED, at open time, never captured at init: Intelligems assigns 2.5-5s
   after load, and a menu opened before that shows arm A whatever the
   assignment later says. The latch only sets once the event has fired, so
   an open before navTest lands is not lost - the next open reports. Gated on
   the running flag so the inert default costs nothing. */
let navMenuOpenReported = false;
const reportNavMenuOpen = () => {
  if (navMenuOpenReported) return;
  if (!(window.GD_FLAGS && window.GD_FLAGS.navTest)) return;
  navMenuOpenReported = true;
  const arm = document.documentElement.classList.contains('gd-nav-no-bundle') ? 'b_no_bundle' : 'a_bundle';
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'gah_nav_menu_open', gah_nav_arm: arm });
  } catch (error) {}
  try {
    if (window.clarity) window.clarity('event', 'gah_nav_menu_open');
  } catch (error) {}
};

/* GAH-PROMO-01: the moment a visitor actually taps the sale bar, in either
   arm, once per pageview - the one direct behavioural read a banner copy
   test has. The exposure event (GD_FLAGS.exposePromoArm in
   layout/theme.liquid) fires on every pageview because the bar is sitewide;
   this second event lets the readout segment to the visitors the bar moved.
   Same arm and channel fields, same values. Reads the class that RENDERED,
   at tap time, never captured at init: Intelligems assigns 2.5-5s after
   load, and a bar tapped before that shows arm A whatever the assignment
   later says. The latch only sets once the event has fired, so a tap before
   promoTest lands is not lost - the next tap reports. Gated on the running
   flag so the inert default costs nothing. */
let promoBarClickReported = false;
const reportPromoBarClick = () => {
  if (promoBarClickReported) return;
  if (!(window.GD_FLAGS && window.GD_FLAGS.promoInTest && window.GD_FLAGS.promoInTest())) return;
  promoBarClickReported = true;
  const promoRoot = document.documentElement;
  const arm = promoRoot.classList.contains('gd-promo-channel') ? 'b_channel' : 'a_labor';
  const channel = promoRoot.dataset.gdChannel || 'none';
  // track() polls for Clarity, which boots on first interaction here - a bare
  // window.clarity check drops the event when this tap IS that interaction
  window.GD_FLAGS.track('gah_promo_bar_click', { gah_promo_arm: arm, gah_promo_channel: channel });
};

/* GAH-FALL-01: the reportPromoBarClick shape - the moment a visitor taps the
   sale bar, in any arm, once per pageview. The exposure fires on every
   pageview (the bar is sitewide); this is the one direct behavioural read a
   banner test has. The arm is read from what RENDERED at tap time. The latch
   only sets once the event has fired, and it is gated on the running flag so
   the inert default costs nothing. */
let fallBarClickReported = false;
const reportFallBarClick = () => {
  if (fallBarClickReported) return;
  if (!(window.GD_FLAGS && window.GD_FLAGS.fallTest === true && window.GD_FLAGS.fallArmName)) return;
  fallBarClickReported = true;
  // track() polls for Clarity, which boots on first interaction here - a bare
  // window.clarity check drops the event when this tap IS that interaction
  window.GD_FLAGS.track('gah_fall_bar_click', { gah_fall_arm: window.GD_FLAGS.fallArmName() });
};

const LOGO_SHINE_CLASS = 'header-benchmark__logo--shine';

// the shine is the site's arrival moment, so it waits for the page to settle
// rather than firing into a half-painted header. on product templates that
// also means waiting out the shimmer overlay, which covers the bar for up to
// 3.2s - a sweep spent behind a white screen is a sweep nobody sees
const playLogoShineOnLoad = (logo) => {
  if (!logo || !logo.querySelector('.header-benchmark__logo-shine')) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const play = () => {
    // animationend on the ::before bubbles to the anchor, and clearing the
    // class puts the band back at opacity 0 so it can be re-armed later
    logo.addEventListener('animationend', () => logo.classList.remove(LOGO_SHINE_CLASS), {
      once: true,
    });
    logo.classList.add(LOGO_SHINE_CLASS);
  };

  const shimmerPending = () => document.documentElement.classList.contains('gd-page-shimmer-pending');

  const start = () => {
    if (!shimmerPending()) {
      setTimeout(play, 200);
      return;
    }
    const observer = new MutationObserver(() => {
      if (shimmerPending()) return;
      observer.disconnect();
      // the overlay still fades for 260ms after the pending class drops
      setTimeout(play, 320);
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  };

  if (document.readyState === 'complete') {
    start();
  } else {
    window.addEventListener('load', start, { once: true });
  }
};

class HeaderBenchmark extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('#Details-HeaderBenchmarkDrawer');
    this.mainView = this.querySelector('.header-benchmark__panel-view--main');
    this.shopView = this.querySelector('.header-benchmark__panel-view--shop');
    this.dropdown = this.querySelector('#HeaderBenchmarkDropdown');
    this.navToggle = this.querySelector('.header-benchmark__shop-toggle--nav');

    // global.js's summary-click handler only toggles a CSS overflow class,
    // which iOS Safari ignores for touch panning — the page kept scrolling
    // behind the open menu. The details 'toggle' event fires on every open
    // and every close path (summary tap, scrim, Escape), so the shared lock
    // can never be left engaged. Do not touch global.js's handler: it serves
    // the whole site; just clear its class if a close path bypassed it.
    if (this.drawer) {
      this.drawer.addEventListener('toggle', () => {
        if (this.drawer.open) {
          if (window.BMScrollLock) {
            window.BMScrollLock.lock('header-menu');
            // global.js put overflow-hidden-tablet on <body> on the summary
            // click, before this toggle fired. Under the lock's
            // html{overflow:hidden} that class makes <body> a scroll container,
            // which re-anchors the sticky header to the -Npx-shifted body and
            // takes the bar and this panel off the top of the screen. The
            // shared lock is the only scroll stop needed here; if it failed to
            // load, global.js's class stays as the fallback.
            document.body.classList.remove('overflow-hidden-tablet');
          }
        } else {
          window.BMScrollLock?.unlock('header-menu');
          document.body.classList.remove('overflow-hidden-tablet');
        }
      });
      // its own listener, so a warm-up failure can never skip the lock above
      this.drawer.addEventListener('toggle', () => {
        if (this.drawer.open) this.warmShopImages('drawer');
      });
      // the drawer only exists behind the mobile nav; close it when the
      // viewport crosses to desktop so its scroll lock cannot linger over
      // a layout that no longer shows the menu
      window.matchMedia('(min-width: 990px)').addEventListener('change', (event) => {
        if (event.matches && this.drawer.open) this.drawer.removeAttribute('open');
      });
    }

    const summary = this.querySelector('#Details-HeaderBenchmarkDrawer summary');
    const scrim = this.querySelector('.header-benchmark__scrim');
    if (summary && scrim) {
      scrim.addEventListener('click', () => {
        if (this.dropdown && !this.dropdown.hidden) {
          this.closeDropdown();
        } else {
          summary.click();
        }
      });
    }

    const badge = this.querySelector('.header-benchmark__cart-count');
    const badgeText = this.querySelector('.js-cart-count-bubble-text');
    if (badge) {
      const placeholder = parseInt(badge.dataset.emptyCount, 10) || 0;
      // global.js rewrites every .js-cart-count-bubble with the live cart count
      // on each cart change; an empty basket falls back to the design's
      // placeholder, and with no placeholder the bubble hides instead of
      // printing a bare 0
      const syncBadge = () => {
        const live = parseInt(badge.textContent.trim(), 10) || 0;
        const shown = live > 0 ? live : placeholder;
        if (badge.textContent.trim() !== String(shown)) badge.textContent = shown;
        badge.classList.toggle('header-benchmark__cart-count--hidden', shown <= 0);
        if (badgeText && !badgeText.textContent.includes(String(shown))) {
          badgeText.textContent = badgeText.textContent.replace(/\d+/, shown);
        }
      };
      const observer = new MutationObserver(syncBadge);
      observer.observe(badge, { childList: true, characterData: true, subtree: true });
      syncBadge();
    }

    startSaleCountdown();
    playLogoShineOnLoad(this.querySelector('.header-benchmark__logo'));

    // on product pages the sale band scrolls to the page's own buybox instead
    // of navigating; the href stays as the fallback for product templates
    // without a [data-buybox] (old non-benchmark PDPs)
    // GAH-PROMO-01: every bar, product template or not - the off-PDP bar is a
    // plain navigation and the listener must not stop it, so no preventDefault
    const promoBar = this.querySelector('.header-benchmark__salebar');
    if (promoBar) {
      promoBar.addEventListener('click', reportPromoBarClick);
      promoBar.addEventListener('click', reportFallBarClick);
    }

    const salebar = this.querySelector('.header-benchmark__salebar[data-scroll-to-buybox]');
    if (salebar) {
      salebar.addEventListener('click', (event) => {
        const buybox = document.querySelector('[data-buybox]');
        if (!buybox) return;
        event.preventDefault();
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        buybox.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      });
    }

    if (this.dropdown && this.navToggle) {
      // desktop: hovering or focusing Shop is the step before the click
      this.navToggle.addEventListener('pointerenter', () => this.warmShopImages('dropdown'), { once: true });
      this.navToggle.addEventListener('focus', () => this.warmShopImages('dropdown'), { once: true });
      this.navToggle.addEventListener('click', () => {
        if (this.dropdown.hidden) {
          this.openDropdown();
        } else {
          this.closeDropdown();
        }
      });
      this.abortController = new AbortController();
      document.addEventListener('click', (event) => {
        if (this.dropdown.hidden || this.contains(event.target)) return;
        this.closeDropdown();
      }, { signal: this.abortController.signal });
      // the dropdown only exists behind the desktop nav; close it when the
      // viewport crosses into mobile so it cannot linger over the drawer UI
      window.matchMedia('(min-width: 990px)').addEventListener('change', (event) => {
        if (!event.matches && !this.dropdown.hidden) this.closeDropdown();
      }, { signal: this.abortController.signal });
      this.addEventListener('keyup', (event) => {
        if (event.code === 'Escape' && !this.dropdown.hidden) {
          this.closeDropdown();
          this.navToggle.focus();
        }
      });
    }

    if (!this.shopView) return;

    this.backButton = this.querySelector('.header-benchmark__submenu-back');
    this.mainView.querySelectorAll('.header-benchmark__shop-toggle').forEach((toggle) => {
      toggle.addEventListener('click', () => this.showShop(toggle));
    });
    this.backButton.addEventListener('click', () => this.showMain());
    this.drawer.addEventListener('toggle', () => {
      if (!this.drawer.open) this.showMain();
    });
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort();
  }

  // the shop cards are lazy and live in a hidden view, so left alone they
  // only start downloading on the frame the cards deal in. Flipping them to
  // eager a step earlier (drawer open, Shop hover/focus) lets them land
  // finished. The drawer and the dropdown print the same cards, so each
  // surface warms only its own copy, once. showShop/openDropdown call it too,
  // for a drawer that opened before this deferred script connected. NAV-01 arm B
  // (html.gd-nav-no-bundle, read only) hides the bundle card and turns the
  // GAH card into a full-width hero Liquid cannot see, so that card's sizes
  // widen before it picks a candidate.
  warmShopImages(surface) {
    try {
      const latch = surface === 'drawer' ? 'shopWarmDrawer' : 'shopWarmDropdown';
      if (this[latch]) return;
      this[latch] = true;
      const scope = this.querySelector(surface === 'drawer' ? '.header-benchmark__cards' : '.header-benchmark__dropdown-cards');
      if (!scope) return;
      const noBundle = document.documentElement.classList.contains('gd-nav-no-bundle');
      if (noBundle) {
        const hero = scope.querySelector('.header-benchmark__card--bundle + .header-benchmark__card .header-benchmark__card-img');
        if (hero) hero.sizes = '(max-width: 989px) calc(100vw - 24px), 340px';
      }
      scope.querySelectorAll('.header-benchmark__card-img[loading="lazy"]').forEach((img) => {
        if (noBundle && img.closest('.header-benchmark__card--bundle')) return;
        img.loading = 'eager';
      });
    } catch (error) {
      // a missed warm-up only means the images load on reveal, as before
    }
  }

  openDropdown() {
    this.warmShopImages('dropdown');
    this.dropdown.hidden = false;
    this.navToggle.setAttribute('aria-expanded', 'true');
    reportNavMenuOpen();
  }

  closeDropdown() {
    this.dropdown.hidden = true;
    this.navToggle.setAttribute('aria-expanded', 'false');
  }

  showShop(toggle) {
    this.warmShopImages('drawer');
    this.mainView.hidden = true;
    this.shopView.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');
    this.backButton.focus();
    reportNavMenuOpen();
  }

  showMain() {
    this.mainView.hidden = false;
    this.shopView.hidden = true;
    this.mainView.querySelectorAll('.header-benchmark__shop-toggle').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false');
    });
  }
}

customElements.define('header-benchmark', HeaderBenchmark);
